import { useEffect, useMemo, useState } from 'react';
import {
  TEMPLATE_KEY_FEATURES,
  type FixedKey, type HandsetLayout, type PhoneFunction, type TemplateKey, type TemplateKeyInput,
  type TemplateKeyKind, type TemplateKeyPanel, type TemplatePresetKey,
} from '@arcnet/contracts';
import { useTemplatePreview } from '../../lib/api/hooks-templates.js';
import { ErrorBanner, Segmented, CardHead } from '../../components/ui/Primitives.js';
import { FIXED_KEY_NAMES, HandsetFace, linePaging, type FaceKey } from './HandsetFace.js';

/**
 * A template's keys as the editor holds them, and the drawn editor for them (docs/29 decision
 * 14): the phone on the left, what the chosen key does on the right, and underneath, what a phone
 * on the template is given for its keys, from the API's own generator.
 */

/** One key. Kind '' is a key left alone: empty on a line key, as the phone has it otherwise. */
export interface KeyDraft {
  kind: TemplateKeyKind | '';
  extension_id: string | null;
  value: string;
  label: string;
}

export const EMPTY_KEY: KeyDraft = { kind: '', extension_id: null, value: '', label: '' };

/** Where a key is: 'line:7', 'fixed:12'. */
export type Slot = string;
export type KeyMap = Record<Slot, KeyDraft>;

export const slotOf = (panel: TemplateKeyPanel, position: number): Slot => `${panel}:${position}`;

export function parseSlot(slot: Slot): { panel: TemplateKeyPanel; position: number } {
  const [panel, position] = slot.split(':');
  return { panel: panel === 'fixed' ? 'fixed' : 'line', position: Number(position) };
}

export const keysFromTemplate = (keys: readonly TemplateKey[]): KeyMap =>
  Object.fromEntries(keys.map((k) => [slotOf(k.panel, k.position), {
    kind: k.kind, extension_id: k.extension_id, value: k.value ?? '', label: k.label ?? '',
  }]));

/** A starting layout, less what the model cannot have: keys past its last, and pages it cannot open. */
export const keysFromPreset = (
  keys: readonly TemplatePresetKey[], lineKeys: number | null, xmlBrowser: boolean,
): KeyMap => Object.fromEntries(keys
  .filter((k) => lineKeys === null || k.position <= lineKeys)
  .filter((k) => xmlBrowser || (k.kind !== 'desk_login' && k.kind !== 'desk_logout'))
  .map((k) => [slotOf('line', k.position), {
    kind: k.kind, extension_id: null, value: k.value ?? '', label: k.label ?? '',
  }]));

/** What the API is sent, line keys first, so an error's index finds its key again. */
export function keysToInput(map: KeyMap): Array<{ slot: Slot; key: TemplateKeyInput }> {
  return Object.entries(map)
    .filter(([, k]) => k.kind !== '')
    .map(([slot, k]) => ({ slot, k, ...parseSlot(slot) }))
    .sort((a, b) => (a.panel === b.panel ? a.position - b.position : a.panel === 'line' ? -1 : 1))
    .map(({ slot, k, panel, position }) => ({
      slot,
      key: {
        panel,
        position,
        kind: k.kind as TemplateKeyKind,
        // The phone's own extension is its only line (owner, 15 September 2026).
        account: k.kind === 'line' ? 1 : null,
        extension_id: k.kind === 'blf' ? k.extension_id : null,
        value: k.kind === 'speed_dial' || k.kind === 'feature' || k.kind === 'phone' ? k.value || null : null,
        label: k.label.trim() || null,
      },
    }));
}

/** The key a `keys.N.field` error from a save names, by what was sent. */
export function errorsBySlot(
  fieldErrors: Readonly<Record<string, string>>, sent: ReadonlyArray<{ slot: Slot }>,
): Record<Slot, string> {
  const out: Record<Slot, string> = {};
  for (const [field, message] of Object.entries(fieldErrors)) {
    const [head, index] = field.split('.');
    const slot = head === 'keys' ? sent[Number(index)]?.slot : undefined;
    if (slot && !out[slot]) out[slot] = message;
  }
  return out;
}

export const KIND_LABEL: Readonly<Record<TemplateKeyKind, string>> = {
  line: 'Line', blf: 'Busy lamp', blf_next: 'Next colleague', speed_dial: 'Speed dial',
  feature: 'Feature', desk_login: 'Log in', desk_logout: 'Log out', phone: 'Phone function',
  none: 'Nothing',
};

export const FEATURE_LABEL: Readonly<Record<(typeof TEMPLATE_KEY_FEATURES)[number], string>> = {
  voicemail_own: 'Voicemail', dnd_on: 'DND on', dnd_off: 'DND off', fwd_off: 'Forward off',
  queue_pause: 'Pause', queue_unpause: 'Resume',
  // The seat's own other phone (0103). Said as the person would: they named the phone under
  // "Send my calls to", and the key is how they reach for it without dialling anything.
  fwd_self_toggle: 'Send my calls to my other phone',
  handoff_self: 'Move this call to my other phone',
};

/** What each of the phone's own functions is, in the key panel. */
const FUNCTION_LABEL: Readonly<Record<PhoneFunction, string>> = {
  history: 'Call history', directory: 'Directory', dnd: 'Do not disturb, the phone’s own',
  menu: 'Menu', status: 'Status', account_up: 'Previous account', account_down: 'Next account',
  forward: 'Forwarding, the phone’s own', recall: 'Call back the last caller', hold: 'Hold',
  transfer: 'Transfer', conference: 'Conference', phone_lock: 'Lock the phone',
};

/** As a screen would show it. */
const FUNCTION_SHORT: Readonly<Record<PhoneFunction, string>> = {
  history: 'History', directory: 'Directory', dnd: 'DND', menu: 'Menu', status: 'Status',
  account_up: 'Acct up', account_down: 'Acct down', forward: 'Forward', recall: 'Recall',
  hold: 'Hold', transfer: 'Transfer', conference: 'Conf', phone_lock: 'Lock',
};

export interface Seat { id: string; number: string; display_name: string }

/** What a key shows when the template gives it no label. */
export function derivedLabel(k: KeyDraft, seats: readonly Seat[]): string {
  switch (k.kind) {
    case 'line': return 'Extension';
    case 'blf': return seats.find((s) => s.id === k.extension_id)?.display_name ?? 'Busy lamp';
    case 'blf_next': return 'Next colleague';
    case 'speed_dial': return k.value || 'Speed dial';
    case 'feature': return FEATURE_LABEL[k.value as keyof typeof FEATURE_LABEL] ?? 'Feature';
    case 'desk_login': return 'Log in';
    case 'desk_logout': return 'Log out';
    case 'phone': return FUNCTION_SHORT[k.value as PhoneFunction] ?? 'Phone';
    default: return '';
  }
}

/** A value that settles: the preview asks once typing stops, not at every key. */
function useSettled<T>(value: T, ms: number): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return settled;
}

export function PhoneKeysEditor({
  model, vendor, layout, keys, onChange, writable, xmlBrowser, allowNamed, seats, errors,
}: {
  model: string;
  /** The make's name, for the hint under the drawing. */
  vendor: string;
  layout: HandsetLayout;
  keys: KeyMap;
  onChange: (next: KeyMap) => void;
  writable: boolean;
  /** Whether the model can open the hot desk pages. */
  xmlBrowser: boolean;
  /** Busy lamps for named extensions: an account's own template only. */
  allowNamed: boolean;
  seats: readonly Seat[];
  errors: Readonly<Record<Slot, string>>;
}) {
  const { slots } = linePaging(layout, false);
  const total = layout.line_keys.total;
  const touch = layout.face === 'touch';
  const usedMax = Math.max(0, ...Object.entries(keys)
    .filter(([slot, k]) => k.kind !== '' && slot.startsWith('line:'))
    .map(([slot]) => parseSlot(slot).position));
  const [askedForPages, setAskedForPages] = useState(false);
  const pageable = total > slots;
  const showPages = pageable && (askedForPages || usedMax > slots);
  const { perPage, pages } = linePaging(layout, showPages);
  const [page, setPage] = useState(1);
  // A T30 has no line keys: start on its first own key.
  const [selected, setSelected] = useState<Slot>(() =>
    (total > 0 ? 'line:1' : slotOf('fixed', layout.fixed[0]?.index ?? 1)));
  useEffect(() => { if (page > pages) setPage(1); }, [page, pages]);

  // A key with an error is the one to look at.
  const firstError = Object.keys(errors)[0];
  useEffect(() => { if (firstError) setSelected(firstError); }, [firstError]);

  const lineFace = (n: number): FaceKey => {
    const k = keys[slotOf('line', n)] ?? EMPTY_KEY;
    const label = k.kind === '' ? '' : k.label.trim() || derivedLabel(k, seats);
    return {
      label, assigned: k.kind !== '',
      describe: `Line key ${n}: ${k.kind === '' ? 'nothing' : `${KIND_LABEL[k.kind]}, ${label}`}`,
    };
  };

  const fixedFace = (key: FixedKey): FaceKey => {
    const f = layout.fixed.find((x) => x.key === key)!;
    const k = keys[slotOf('fixed', f.index)] ?? EMPTY_KEY;
    const standing = f.default ? FUNCTION_SHORT[f.default] : f.own ?? '';
    const label = k.kind === '' ? (f.default ? standing : '')
      : k.kind === 'none' ? ''
      : k.label.trim() || derivedLabel(k, seats);
    const does = k.kind === '' ? `as the phone has it, ${standing || 'nothing'}`
      : k.kind === 'none' ? (f.own ? `only ${f.own}` : 'nothing')
      : `${KIND_LABEL[k.kind]}, ${label}`;
    return { label, assigned: k.kind !== '', describe: `${FIXED_KEY_NAMES[key]}: ${does}` };
  };

  // A drawing with nothing to set yet (Fanvil, the cordless handsets): shown, not edited.
  const settable = total > 0 || layout.fixed.length > 0;
  const { panel, position } = parseSlot(selected);
  const where = panel === 'fixed'
    ? 'On the home screen. In a call it does its usual job.'
    : showPages ? `Page ${Math.ceil(position / perPage)}` : touch ? 'On the screen' : 'Beside the screen';

  const firstKey = (page - 1) * perPage + 1;
  return (
    <div className="arc-phone-editor">
      <div style={{ minWidth: 0 }}>
        <div className="arc-row" style={{ gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
          {showPages && (
            <Segmented label="Line key page" value={page} onChange={setPage}
              options={Array.from({ length: pages }, (_, i) => [i + 1, `Page ${i + 1}`] as const)} />
          )}
          {pageable && !showPages && (
            <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" onClick={() => setAskedForPages(true)}>
              More line keys
            </button>
          )}
          <span className="arc-subtle" style={{ fontSize: 11.5 }}>
            {total === 0 ? 'No line keys on this model.'
              : showPages
                ? `Keys ${firstKey} to ${Math.min(firstKey + perPage - 1, total)}. ${touch ? 'More' : 'The bottom right key'} turns the page.`
                : pageable ? `${slots} line keys, ${total} with pages.` : `${total} line key${total === 1 ? '' : 's'}.`}
          </span>
        </div>
        <div className="arc-phone-wrap">
          <HandsetFace layout={layout} model={model} page={page} showPages={showPages}
            line={lineFace} fixed={fixedFace} selected={selected}
            onSelect={setSelected} onPage={setPage} />
        </div>
        <p className="arc-hint" style={{ marginTop: 8 }}>
          {layout.verified
            ? `Checked on a ${model} on ${layout.verified}.`
            : !settable
              ? `Drawn from ${vendor}’s product photos. Its keys cannot be set here yet.`
              : `Drawn from ${vendor}’s documentation and product photos, not yet checked on a ${model}.`}
        </p>
      </div>

      {settable && (
        <KeyPanel key={selected} slot={selected} where={where} layout={layout}
          draft={keys[selected] ?? EMPTY_KEY} writable={writable} xmlBrowser={xmlBrowser}
          allowNamed={allowNamed} seats={seats} error={errors[selected] ?? null}
          onSet={(next) => onChange({ ...keys, [selected]: next })} />
      )}
    </div>
  );
}

function KeyPanel({ slot, where, layout, draft, writable, xmlBrowser, allowNamed, seats, error, onSet }: {
  slot: Slot; where: string; layout: HandsetLayout; draft: KeyDraft;
  writable: boolean; xmlBrowser: boolean; allowNamed: boolean; seats: readonly Seat[];
  error: string | null;
  onSet: (next: KeyDraft) => void;
}) {
  const { panel, position } = parseSlot(slot);
  const f = panel === 'fixed' ? layout.fixed.find((x) => x.index === position) ?? null : null;
  const title = f ? FIXED_KEY_NAMES[f.key] : `Line key ${position}`;
  const functions = panel === 'line' ? layout.functions.line : layout.functions.fixed;

  const desk: Array<[string, string]> = xmlBrowser ? [['desk_login', 'Log in'], ['desk_logout', 'Log out']] : [];
  const options: Array<[string, string]> = panel === 'line'
    ? [
        ['', 'Nothing'],
        ['line', 'The phone’s extension'],
        ...(allowNamed ? [['blf', 'Busy lamp'] as [string, string]] : []),
        ['blf_next', 'Next colleague'],
        ['speed_dial', 'Speed dial'],
        ['feature', 'Feature code'],
        ...desk,
        ['phone', 'Phone function'],
      ]
    : [
        ['', f?.default ? `As the phone has it: ${FUNCTION_LABEL[f.default]}` : `Its usual job: ${f?.own ?? ''}`],
        // Type 0 on a key with no default is the same as leaving it alone.
        ...(f?.default ? [['none', f.own ? `Only its usual job: ${f.own}` : 'Nothing'] as [string, string]] : []),
        ['speed_dial', 'Speed dial'],
        ['feature', 'Feature code'],
        ...desk,
        ['phone', 'Phone function'],
      ];
  // A kind the key can no longer have is shown, so saving says why rather than the list lying.
  if (draft.kind !== '' && !options.some(([v]) => v === draft.kind)) options.push([draft.kind, KIND_LABEL[draft.kind]]);

  const set = (next: Partial<KeyDraft>) => onSet({ ...draft, ...next });
  const labelled = draft.kind !== '' && draft.kind !== 'none' && (panel === 'line' || f?.labelled === true);

  return (
    <div className="arc-key-panel">
      <div className="arc-key-panel-head">
        <span className="arc-key-chip">{panel === 'line' ? position : f?.index ?? '·'}</span>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 600 }}>{title}</div>
          <div className="arc-subtle" style={{ fontSize: 11.5 }}>{where}</div>
        </div>
      </div>

      <div className="arc-field">
        <label className="arc-label" htmlFor="key-does">Does</label>
        <select id="key-does" className="arc-select" value={draft.kind} disabled={!writable}
          onChange={(e) => onSet({ ...EMPTY_KEY, kind: e.target.value as KeyDraft['kind'] })}>
          {options.map(([v, text]) => <option key={v} value={v}>{text}</option>)}
        </select>
      </div>

      {draft.kind === 'blf' && (
        <div className="arc-field">
          <label className="arc-label" htmlFor="key-who">Whose lamp</label>
          <select id="key-who" className="arc-select" value={draft.extension_id ?? ''} disabled={!writable}
            onChange={(e) => set({ extension_id: e.target.value || null })}>
            <option value="" disabled>Choose an extension…</option>
            {seats.map((s) => <option key={s.id} value={s.id}>{s.number} · {s.display_name}</option>)}
          </select>
        </div>
      )}
      {draft.kind === 'speed_dial' && (
        <div className="arc-field">
          <label className="arc-label" htmlFor="key-number">Number</label>
          <input id="key-number" className="arc-input arc-mono" value={draft.value} disabled={!writable}
            inputMode="tel" placeholder="0115550100"
            onChange={(e) => set({ value: e.target.value.replace(/[^0-9*#+]/g, '').slice(0, 32) })} />
        </div>
      )}
      {draft.kind === 'feature' && (
        <div className="arc-field">
          <label className="arc-label" htmlFor="key-feature">Feature</label>
          <select id="key-feature" className="arc-select" value={draft.value} disabled={!writable}
            onChange={(e) => set({ value: e.target.value })}>
            <option value="" disabled>Choose a feature…</option>
            {TEMPLATE_KEY_FEATURES.map((x) => <option key={x} value={x}>{FEATURE_LABEL[x]}</option>)}
          </select>
          <p className="arc-hint">Dials the account’s own code for it.</p>
        </div>
      )}
      {draft.kind === 'phone' && (
        <div className="arc-field">
          <label className="arc-label" htmlFor="key-function">Function</label>
          <select id="key-function" className="arc-select" value={draft.value} disabled={!writable}
            onChange={(e) => set({ value: e.target.value })}>
            <option value="" disabled>Choose what it opens…</option>
            {functions.map((x) => <option key={x} value={x}>{FUNCTION_LABEL[x]}</option>)}
          </select>
        </div>
      )}
      {draft.kind === 'line' && <p className="arc-hint">The extension the phone is set up on.</p>}
      {draft.kind === 'blf_next' && <p className="arc-hint">The next colleague in number order, on each phone.</p>}
      {draft.kind === 'desk_login' && (
        <p className="arc-hint">Makes the phone a shared desk: blank until somebody logs in, and this key logs them off.</p>
      )}

      {labelled && (
        <div className="arc-field">
          <label className="arc-label" htmlFor="key-label">Label</label>
          <input id="key-label" className="arc-input" value={draft.label} maxLength={32} disabled={!writable}
            placeholder={derivedLabel(draft, seats)} onChange={(e) => set({ label: e.target.value })} />
        </div>
      )}

      {error && <div className="arc-error">{error}</div>}
    </div>
  );
}

/** "4, 6 to 9": positions, run together. */
function runs(ns: readonly number[]): string {
  const out: string[] = [];
  let i = 0;
  while (i < ns.length) {
    let j = i;
    while (j + 1 < ns.length && ns[j + 1] === ns[j]! + 1) j++;
    out.push(j > i ? `${ns[i]} to ${ns[j]}` : String(ns[i]));
    i = j + 1;
  }
  return out.join(', ');
}

/**
 * What a phone on the template is given for its keys (the Configuration Generator's preview),
 * from the API's generator with a sample line and the account's names.
 */
export function KeySettings({ tenantId, modelId, keys }: {
  tenantId: string | null; modelId: string; keys: KeyMap;
}) {
  const body = useMemo(() => JSON.stringify(keysToInput(keys).map((x) => x.key)), [keys]);
  const settled = useSettled(body, 400);
  const preview = useTemplatePreview(tenantId, modelId, settled);
  const d = preview.data;
  const text = d
    ? [...d.lines, ...(d.cleared.length > 0 ? [`# linekey ${runs(d.cleared)} emptied`] : [])].join('\n')
    : 'Reading…';

  return (
    <div className="arc-card" style={{ marginTop: 14 }}>
      <CardHead title="Settings" sub="What a phone on this template is given for its keys." />
      <div className="arc-card-body">
        {preview.error ? <ErrorBanner error={preview.error} /> : (
          <pre className="arc-phone-settings" aria-busy={preview.isFetching}>{text}</pre>
        )}
        {d?.warnings.map((w) => <p key={w} className="arc-hint" style={{ marginTop: 6 }}>{w}</p>)}
      </div>
    </div>
  );
}
